import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@learn-me/ui";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  Settings,
  FileText,
  ListTree,
  Sparkles,
  ClipboardCheck,
  Send,
} from "lucide-react";
import { useCourseWizard } from "@/hooks";
import { WizardStep1Config } from "@/features/admin/components/wizard/WizardStep1Config";
import { WizardStep2References } from "@/features/admin/components/wizard/WizardStep2References";
import { WizardStep3Structure } from "@/features/admin/components/wizard/WizardStep3Structure";
import { WizardStep4Generation } from "@/features/admin/components/wizard/WizardStep4Generation";
import { WizardStep5Review } from "@/features/admin/components/wizard/WizardStep5Review";
import { WizardStep6Publish } from "@/features/admin/components/wizard/WizardStep6Publish";

const stepIcons = {
  1: Settings,
  2: FileText,
  3: ListTree,
  4: Sparkles,
  5: ClipboardCheck,
  6: Send,
};

export default function CourseCreationWizardPage() {
  const navigate = useNavigate();
  const {
    currentStep,
    nextStep,
    prevStep,
    reset,
    canProceed,
    stepLabels,
    stepDescriptions,
    completionPercentage,
    courseId,
  } = useCourseWizard();

  // Redirect to course editor if course was created
  useEffect(() => {
    if (currentStep === 6 && courseId) {
      // Course created successfully, can navigate to editor
    }
  }, [currentStep, courseId]);

  const handleCancel = () => {
    if (
      window.confirm(
        "¿Estas seguro de que quieres cancelar? Se perdera todo el progreso.",
      )
    ) {
      reset();
      navigate("/admin/courses");
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      handleCancel();
    } else {
      prevStep();
    }
  };

  const handleNext = () => {
    if (canProceed()) {
      nextStep();
    }
  };

  const handleFinish = () => {
    if (courseId) {
      reset();
      navigate(`/admin/courses/${courseId}`);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WizardStep1Config />;
      case 2:
        return <WizardStep2References />;
      case 3:
        return <WizardStep3Structure />;
      case 4:
        return <WizardStep4Generation />;
      case 5:
        return <WizardStep5Review />;
      case 6:
        return <WizardStep6Publish onFinish={handleFinish} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Crear nuevo curso con AI
                </h1>
                <p className="text-sm text-gray-500">
                  Paso {currentStep} de 6: {stepLabels[currentStep]}
                </p>
              </div>
            </div>

            {/* Progress indicator */}
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {completionPercentage}% completado
              </span>
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${completionPercentage}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-between mt-4 px-2">
            {[1, 2, 3, 4, 5, 6].map((step) => {
              const Icon = stepIcons[step as keyof typeof stepIcons];
              const isActive = step === currentStep;
              const isCompleted = step < currentStep;
              const isPending = step > currentStep;

              return (
                <div
                  key={step}
                  className={`flex items-center ${step < 6 ? "flex-1" : ""}`}
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        isActive
                          ? "bg-primary-500 text-white"
                          : isCompleted
                            ? "bg-green-500 text-white"
                            : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </div>
                    <span
                      className={`text-xs mt-1 hidden md:block ${
                        isActive
                          ? "text-primary-600 font-medium"
                          : isPending
                            ? "text-gray-400"
                            : "text-gray-600"
                      }`}
                    >
                      {stepLabels[step as keyof typeof stepLabels]}
                    </span>
                  </div>
                  {step < 6 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${
                        isCompleted ? "bg-green-500" : "bg-gray-200"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Step description */}
        <div className="mb-6 text-center">
          <p className="text-gray-600">
            {stepDescriptions[currentStep as keyof typeof stepDescriptions]}
          </p>
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer navigation */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {currentStep === 1 ? "Cancelar" : "Atras"}
          </Button>

          {currentStep < 6 && (
            <Button onClick={handleNext} disabled={!canProceed()}>
              {currentStep === 5 ? "Finalizar" : "Siguiente"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </footer>

      {/* Bottom padding for fixed footer */}
      <div className="h-20" />
    </div>
  );
}
